import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAnalysisStore } from '../store/useAnalysisStore';
import { reportApi } from '../assets/api/apis';
import { kidsQueries } from '../queries';
import {
  ANALYSIS_STATUS,
  AI_JOB_STATUS,
  VIDEO_RECORD_TYPE,
} from '../data/define';
import type { AnalysisLocationState } from '../page/registerkids/analysisResult';

export function useAiJobPolling(
  jobId: number | null,
  locationState: AnalysisLocationState | null
) {
  const { status, info } = useAnalysisStore((s) => s.analysisResult);
  const setAnalysisResult = useAnalysisStore((s) => s.setAnalysisResult);
  const setLoadingProgress = useAnalysisStore((s) => s.setLoadingProgress);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // AI Job 상태 조회 & 폴링
  const { data: job, error: jobQueryError } = useQuery({
    queryKey: ['aiJobStatus', jobId],
    queryFn: async () => {
      const res = await reportApi.getAiJobStatus(jobId!);
      return res.data;
    },
    enabled: jobId !== null,
    refetchInterval: (query) => {
      // 조회 실패가 4xx(404 등)로 확정되면 오류 화면을 띄운 채 계속 폴링하지 않는다.
      // 네트워크 끊김·5xx 는 일시 장애일 수 있으니 계속 폴링한다
      const httpStatus = (query.state.error as AxiosError | null)?.response?.status;
      if (httpStatus && httpStatus >= 400 && httpStatus < 500) return false;
      const job = query.state.data;
      if (!job) return 3000;
      if (
        job.status === AI_JOB_STATUS.SUCCESS ||
        job.status === AI_JOB_STATUS.FAILED ||
        job.status === AI_JOB_STATUS.CANCELED
      ) {
        return false;
      }
      return 3000;
    },
  });

  // AI Job 상태 동기화 및 에러 핸들링
  useEffect(() => {
    if (jobQueryError) {
      console.error('Error fetching job status:', jobQueryError);
      if (status !== ANALYSIS_STATUS.ERROR) {
        setAnalysisResult({
          status: ANALYSIS_STATUS.ERROR,
          data: null,
          error: jobQueryError instanceof Error ? jobQueryError : new Error(String(jobQueryError)),
          info: null,
        });
      }
      return;
    }

    // 다른 잡의 응답이면 무시한다(이전 분석 결과가 새 분석 화면에 섞이지 않게)
    if (!job || Number(job.id) !== jobId) return;

    if (job.status === AI_JOB_STATUS.SUCCESS) {
      if (status !== ANALYSIS_STATUS.SUCCESS) {
        setAnalysisResult({
          status: ANALYSIS_STATUS.SUCCESS,
          data: { reportId: job.reportId, mediaId: job.mediaId },
          error: null,
          info: {
            kidId: job.kidId,
            mi: locationState?.mi || info?.mi || null,
            reportId: job.reportId,
            type: job.mediaType ? VIDEO_RECORD_TYPE.DAILY : VIDEO_RECORD_TYPE.QNA,
          },
        });
        setLoadingProgress(job.progress || 100);
        // 분석 전에 받아 둔 아이 상세(리포트 목록)·리포트 상세 캐시를 버린다.
        // 남아 있으면 '리포트 확인' 시 리포트 화면이 이전 목록(0건)을 보고 홈으로 보낸다(user-178)
        queryClient.invalidateQueries({ queryKey: kidsQueries.all });
      }
      if (searchParams.get('jobId') !== String(job.id)) {
        setSearchParams({ jobId: String(job.id) }, { replace: true });
      }
    } else if (job.status === AI_JOB_STATUS.FAILED || job.status === AI_JOB_STATUS.CANCELED) {
      if (status !== ANALYSIS_STATUS.ERROR) {
        setAnalysisResult({
          status: ANALYSIS_STATUS.ERROR,
          data: null,
          error: new Error(job.errorMessage || 'Job failed'),
          info: {
            kidId: job.kidId,
            mi: locationState?.mi || info?.mi || null,
            reportId: job.reportId,
            type: job.mediaType ? VIDEO_RECORD_TYPE.DAILY : VIDEO_RECORD_TYPE.QNA,
          },
        });
      }
    } else {
      if (status !== ANALYSIS_STATUS.PENDING) {
        setAnalysisResult({
          status: ANALYSIS_STATUS.PENDING,
          data: null,
          error: null,
          info: {
            kidId: job.kidId,
            mi: locationState?.mi || info?.mi || null,
            reportId: job.reportId,
            type: job.mediaType ? VIDEO_RECORD_TYPE.DAILY : VIDEO_RECORD_TYPE.QNA,
          },
        });
      }
      if (typeof job.progress === 'number') {
        // 표시값은 줄어들지 않는다(화면 타이머가 서버 단계값보다 앞서 있을 수 있음, user-176)
        setLoadingProgress((prev) => Math.max(prev, job.progress));
      }
    }
  }, [job, jobQueryError, status, locationState, info, setSearchParams, searchParams, setAnalysisResult, setLoadingProgress, queryClient]);

  return { job };
}
